import * as React from "react";
import { type ReactNode } from "react";
import { debounce } from "throttle-debounce";

type Props = typeof InfiniteLoader.defaultProps & {
    onLoad: () => PromiseLike<unknown>;
    loadingIndicator: React.ReactNode;
    hasNextPage: boolean;
    element: HTMLElement;
    children: ReactNode;
};

type State = {
    loading: boolean;
    showLoading: boolean;
};

export default class InfiniteLoader extends React.Component<Props, State> {
    public static defaultProps = {
        topPadding      : 0,
        loadingThreshold: 200,
    };

    protected loadingTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(props: Props) {
        super(props);

        this.state = {
            loading    : false,
            showLoading: false,
        };

        this.handleScroll = debounce(100, this.handleScroll.bind(this), {
            atBegin: false,
        });
        this.checkToLoad = this.checkToLoad.bind(this);
        this.loadMoreData = this.loadMoreData.bind(this);
    }

    public componentDidMount(): void {
        this.props.element.addEventListener("scroll", this.handleScroll);
        this.handleScroll();
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.element !== this.props.element) {
            this.replaceListener(prevProps.element, this.props.element);
        }
    }

    public componentWillUnmount(): void {
        this.props.element.removeEventListener("scroll", this.handleScroll);
    }

    protected replaceListener(prevElement: HTMLElement, newElement: HTMLElement): void {
        prevElement.removeEventListener("scroll", this.handleScroll);
        newElement.addEventListener("scroll", this.handleScroll);

        this.handleScroll();
    }

    public handleScroll(): void {
        if(this.state.loading) return;

        this.checkToLoad();
    }

    protected checkToLoad(): void {
        if(!this.props.hasNextPage) {
            return;
        }

        const scrollTop = this.props.element.scrollTop;
        const bounding = this.props.element.getBoundingClientRect();
        const height = this.props.element.scrollHeight;

        // NOTE: I (Nick) attempted to use events here instead, but currentTarget ends up being null for some reason
        // const position = e.currentTarget.scrollTop + this.props.topPadding;
        // const height   = e.currentTarget.scrollHeight;
        const position = scrollTop + bounding.height + this.props.topPadding;

        if(height - position <= this.props.loadingThreshold) {
            void this.loadMoreData();
        }
    }

    protected loadMoreData(): PromiseLike<void> {
        if(this.state.loading) {
            return Promise.resolve();
        }

        if(this.loadingTimer) {
            clearTimeout(this.loadingTimer);
        }

        this.setState({
            loading    : true,
            showLoading: true,
        });

        return this.props.onLoad().then(() => {
            this.setState({
                loading: false,
            }, this.handleScroll);

            this.loadingTimer = setTimeout(() => {
                this.setState({
                    showLoading: false,
                });
            }, 100);
        });
    }

    public render(): React.ReactNode {
        return (
            <React.Fragment>
                {this.props.children}
                {this.state.showLoading && this.props.loadingIndicator}
            </React.Fragment>
        );
    }
}
